import { Head } from "@inertiajs/react"
import { Link, useForm } from "@inertiajs/react"

import AppLayout from "@/layouts/app-layout"
import type { BreadcrumbItem } from "@/types"

interface CaseRecord {
  id: number
  case_number: string
  display_name: string
}

interface PerDiemRates {
  breakfast: number
  lunch: number
  dinner: number
}

interface Props {
  case_record: CaseRecord
  users: { id: number; name: string }[]
  per_diem_rates: PerDiemRates
  per_diem_type_id: number
  errors?: Record<string, string[]>
}

export default function NewPerDiem({
  case_record,
  users,
  per_diem_rates,
  per_diem_type_id,
  errors,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Cases", href: "/cases" },
    {
      title: case_record.display_name || case_record.case_number,
      href: `/cases/${case_record.id}`,
    },
    { title: "Expenses", href: `/cases/${case_record.id}/expenses` },
    {
      title: "Per Diem",
      href: `/cases/${case_record.id}/expenses/new?per_diem=true`,
    },
  ]

  const { data, setData, post, processing, transform } = useForm({
    user_id: null as number | null,
    expense_date: new Date().toISOString().slice(0, 10),
    per_diem_breakfast: 0,
    per_diem_lunch: 0,
    per_diem_dinner: 0,
  })

  transform((data) => ({
    case_expense: {
      ...data,
      expense_type: "P",
      expense_type_ref_id: per_diem_type_id,
      paid_by: "Cash",
      amount:
        data.per_diem_breakfast + data.per_diem_lunch + data.per_diem_dinner,
    },
  }))

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    post(`/cases/${case_record.id}/expenses`)
  }

  const inputClass =
    "mt-1 block w-full rounded-md border-hpi-light-gray shadow-sm focus:border-hpi-active-blue focus:ring-hpi-active-blue"
  const labelClass = "block text-sm font-medium text-hpi-dark-gray"

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title={`Add Per Diem - ${case_record.case_number}`} />

      <div className="p-6">
        <h1 className="mb-6 text-2xl font-semibold">Add Per Diem</h1>

        <div className="max-w-md rounded-lg bg-white p-6 shadow">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className={labelClass}>Employee</label>
              <select
                className={inputClass}
                value={data.user_id ?? ""}
                onChange={(e) =>
                  setData(
                    "user_id",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
              >
                <option value="">Select employee...</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
              {errors?.user_id && (
                <p className="text-hpi-red mt-1 text-sm">{errors.user_id[0]}</p>
              )}
            </div>

            <div>
              <label className={labelClass}>Date</label>
              <input
                type="date"
                className={inputClass}
                value={data.expense_date}
                onChange={(e) => setData("expense_date", e.target.value)}
              />
              {errors?.expense_date && (
                <p className="text-hpi-red mt-1 text-sm">
                  {errors.expense_date[0]}
                </p>
              )}
            </div>

            <fieldset className="space-y-3">
              <legend className={labelClass}>Meals</legend>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="pd_breakfast"
                  className="border-hpi-light-gray text-hpi-active-blue focus:ring-hpi-active-blue h-4 w-4 rounded"
                  checked={data.per_diem_breakfast > 0}
                  onChange={(e) =>
                    setData(
                      "per_diem_breakfast",
                      e.target.checked ? per_diem_rates.breakfast : 0,
                    )
                  }
                />
                <label
                  htmlFor="pd_breakfast"
                  className="text-hpi-dark-gray ml-2 text-sm"
                >
                  Breakfast - ${per_diem_rates.breakfast.toFixed(2)}
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="pd_lunch"
                  className="border-hpi-light-gray text-hpi-active-blue focus:ring-hpi-active-blue h-4 w-4 rounded"
                  checked={data.per_diem_lunch > 0}
                  onChange={(e) =>
                    setData(
                      "per_diem_lunch",
                      e.target.checked ? per_diem_rates.lunch : 0,
                    )
                  }
                />
                <label
                  htmlFor="pd_lunch"
                  className="text-hpi-dark-gray ml-2 text-sm"
                >
                  Lunch - ${per_diem_rates.lunch.toFixed(2)}
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="pd_dinner"
                  className="border-hpi-light-gray text-hpi-active-blue focus:ring-hpi-active-blue h-4 w-4 rounded"
                  checked={data.per_diem_dinner > 0}
                  onChange={(e) =>
                    setData(
                      "per_diem_dinner",
                      e.target.checked ? per_diem_rates.dinner : 0,
                    )
                  }
                />
                <label
                  htmlFor="pd_dinner"
                  className="text-hpi-dark-gray ml-2 text-sm"
                >
                  Dinner - ${per_diem_rates.dinner.toFixed(2)}
                </label>
              </div>
            </fieldset>

            <div className="flex justify-end space-x-3 pt-2">
              <Link
                href={`/cases/${case_record.id}/expenses`}
                className="border-hpi-light-gray text-hpi-dark-gray hover:bg-hpi-pale-blue rounded-md border bg-white px-4 py-2 text-sm font-medium"
              >
                Cancel
              </Link>
              <button
                type="submit"
                disabled={processing}
                className="bg-hpi-dark-blue hover:bg-hpi-blue rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
              >
                {processing ? "Saving..." : "Add Per Diem"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AppLayout>
  )
}
